// openEHR RM model — BMM package org.openehr.rm.common.archetyped
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { ITEM_STRUCTURE } from "../data_structures/item_structure.ts";
import type { DV_ENCAPSULATED } from "../data_types/encapsulated.ts";
import type { DV_DATE_TIME } from "../data_types/quantity/date_time.ts";
import type { DV_TEXT } from "../data_types/text.ts";
import type { DV_EHR_URI } from "../data_types/uri.ts";
import type { PARTY_IDENTIFIED, PARTY_PROXY } from "./generic.ts";
import * as openehr_base from "../../base/mod.ts";
import { applyRmInit, type RmInit } from "../../base/apply_rm_init.ts";

/**
 * The \`PATHABLE\` class defines the pathing capabilities used by nearly all classes in the openEHR reference model, mostly via inheritance of \`LOCATABLE\`. The defining characteristics of \`PATHABLE\` objects are that they can locate child objects using paths, and they know their parent object in a compositional hierarchy. The parent feature is defined as abstract in the model, and may be implemented in any way convenient.
 */
export abstract class PATHABLE extends openehr_base.Any {
  /**
   * Internal storage for parent reference.
   * @protected
   */
  protected _parent?: PATHABLE;

  /**
   * Parent of this node in a compositional hierarchy.
   * Returns undefined if this is the root node.
   *
   * @returns Parent node or undefined
   */
  parent(): PATHABLE | undefined {
    return this._parent;
  }

  /**
   * Sets the parent of this node.
   * @param parent - The parent node
   */
  setParent(parent: PATHABLE | undefined): void {
    this._parent = parent;
  }

  /**
   * The item at a path (relative to this item); only valid for unique paths, i.e. paths that resolve to a single item.
   * @param a_path - Path string in openEHR path format (e.g., "/content[at0001]/data")
   * @returns Result value at path, or undefined if not found
   */
  item_at_path(a_path: openehr_base.String | string): openehr_base.Any | undefined {
    const pathStr = typeof a_path === "string" ? a_path : a_path.value || "";
    
    // Parse the path and navigate
    const segments = this.parsePath(pathStr);
    if (segments.length === 0) {
      return this;
    }
    
    // Navigate through the path
    // deno-lint-ignore no-this-alias
    let current: any = this;
    for (const segment of segments) {
      if (!current) return undefined;
      current = this.navigateSegment(current, segment);
    }
    
    return current;
  }

  /**
   * Parses an openEHR path into segments.
   * @param path - The path string
   * @returns Array of path segments
   */
  protected parsePath(path: string): Array<{ attribute: string; nodeId?: string }> {
    if (!path || path === "/") return [];
    
    const segments: Array<{ attribute: string; nodeId?: string }> = [];
    // Remove leading slash
    const pathStr = path.startsWith("/") ? path.substring(1) : path;
    
    // Split by "/" but handle [nodeId] correctly
    const parts = pathStr.split("/").filter(p => p.length > 0);
    
    for (const part of parts) {
      // Parse attribute[nodeId] format
      const match = part.match(/^([a-zA-Z_][a-zA-Z0-9_]*)(?:\[([^\]]+)\])?$/);
      if (match) {
        segments.push({
          attribute: match[1],
          nodeId: match[2],
        });
      }
    }
    
    return segments;
  }

  /**
   * Navigate to a child element based on a path segment.
   * @param current - Current object
   * @param segment - Path segment with attribute and optional nodeId
   * @returns The child element, or undefined if not found
   */
  protected navigateSegment(
    current: any,
    segment: { attribute: string; nodeId?: string }
  ): any {
    if (!current) return undefined;
    
    // Get the attribute value
    const attrValue = current[segment.attribute];
    if (attrValue === undefined) return undefined;
    
    // If no nodeId filter, return the attribute value directly
    if (!segment.nodeId) {
      return attrValue;
    }
    
    // If it's an array, find the item with matching archetype_node_id
    if (Array.isArray(attrValue)) {
      return attrValue.find((item: any) => {
        if (item && typeof item === "object" && "archetype_node_id" in item) {
          return item.archetype_node_id === segment.nodeId;
        }
        return false;
      });
    }
    
    // If it's a single object with matching archetype_node_id, return it
    if (attrValue && typeof attrValue === "object" && "archetype_node_id" in attrValue) {
      if (attrValue.archetype_node_id === segment.nodeId) {
        return attrValue;
      }
    }
    
    return undefined;
  }

  /**
   * List of items corresponding to a non-unique path.
   * @param a_path - Parameter
   * @returns List of items matching the path
   */
  items_at_path(a_path: openehr_base.String | string): openehr_base.Any[] {
    const pathStr = typeof a_path === "string" ? a_path : a_path.value || "";
    const results: openehr_base.Any[] = [];
    
    // Parse the path
    const segments = this.parsePath(pathStr);
    if (segments.length === 0) {
      results.push(this);
      return results;
    }
    
    // For paths with wildcards or multiple matches, collect all
    this.collectItemsAtPath(this, segments, 0, results);
    return results;
  }

  /**
   * Recursively collects items at a path.
   */
  protected collectItemsAtPath(
    current: any,
    segments: Array<{ attribute: string; nodeId?: string }>,
    index: number,
    results: any[]
  ): void {
    if (!current || index >= segments.length) {
      if (current !== undefined) {
        results.push(current);
      }
      return;
    }
    
    const segment = segments[index];
    const attrValue = current[segment.attribute];
    
    if (attrValue === undefined) return;
    
    if (Array.isArray(attrValue)) {
      for (const item of attrValue) {
        // Handle wildcard [*] or specific nodeId
        if (!segment.nodeId || segment.nodeId === "*") {
          this.collectItemsAtPath(item, segments, index + 1, results);
        } else if (item && typeof item === "object" && 
                   "archetype_node_id" in item && 
                   item.archetype_node_id === segment.nodeId) {
          this.collectItemsAtPath(item, segments, index + 1, results);
        }
      }
    } else {
      if (!segment.nodeId || segment.nodeId === "*") {
        this.collectItemsAtPath(attrValue, segments, index + 1, results);
      } else if (attrValue && typeof attrValue === "object" && 
                 "archetype_node_id" in attrValue && 
                 attrValue.archetype_node_id === segment.nodeId) {
        this.collectItemsAtPath(attrValue, segments, index + 1, results);
      }
    }
  }

  /**
   * True if the path exists in the data with respect to the current item.
   * @param a_path - Parameter
   * @returns Boolean indicating if path exists
   */
  path_exists(a_path: openehr_base.String | string): openehr_base.Boolean {
    const item = this.item_at_path(a_path);
    return openehr_base.Boolean.from(item !== undefined);
  }

  /**
   * True if the path corresponds to a single item in the data.
   * @param a_path - Parameter
   * @returns Boolean indicating if path is unique
   */
  path_unique(a_path: openehr_base.String | string): openehr_base.Boolean {
    const items = this.items_at_path(a_path);
    return openehr_base.Boolean.from(items.length === 1);
  }

  /**
   * The path to an item relative to the root of this archetyped structure.
   * @param a_loc - The target item
   * @returns String path to the item
   */
  path_of_item(a_loc: PATHABLE): openehr_base.String {
    // Build path by traversing parent chain from target to this node
    const segments: string[] = [];
    let current: PATHABLE | undefined = a_loc;
    
    while (current && current !== this) {
      const segment = this.buildPathSegment(current);
      if (segment) {
        segments.unshift(segment);
      }
      current = current.parent();
    }
    
    // If we didn't reach this node, the item is not a descendant
    if (current !== this) {
      return openehr_base.String.from("");
    }
    
    return openehr_base.String.from("/" + segments.join("/"));
  }

  /**
   * Build a path segment for a node based on its archetype_node_id.
   */
  protected buildPathSegment(node: PATHABLE): string {
    // Check if node has archetype_node_id (is LOCATABLE)
    if ("archetype_node_id" in node && (node as any).archetype_node_id) {
      // Try to determine the attribute name in the parent
      const parent = node.parent();
      if (parent) {
        // Search parent's attributes for this node
        for (const [attr, value] of Object.entries(parent)) {
          if (value === node) {
            return `${attr}[${(node as any).archetype_node_id}]`;
          }
          if (Array.isArray(value) && value.includes(node)) {
            return `${attr}[${(node as any).archetype_node_id}]`;
          }
        }
      }
      return `[${(node as any).archetype_node_id}]`;
    }
    return "";
  }

  /**
   * Default value equality comparison for PATHABLE subclasses.
   * Compares constructor names as a basic implementation.
   * Subclasses should override this for more specific comparisons.
   * @param other - The other object to compare with
   * @returns Boolean wrapper indicating equality
   */
  is_equal(other: any): openehr_base.Boolean {
    if (!(other instanceof PATHABLE)) {
      return openehr_base.Boolean.from(false);
    }
    // Basic implementation: compare constructor names
    // Subclasses should override for property-level comparison
    return openehr_base.Boolean.from(this.constructor.name === other.constructor.name);
  }
}

/**
 * The \`LINK\` type defines a logical relationship between two items, such as two \`ENTRYs\` or an \`ENTRY\` and a \`COMPOSITION\`. Links can be used across compositions, and across EHRs. Links can potentially be used between interior (i.e. non archetype root) nodes, although this probably should be prevented in archetypes. Multiple \`LINKs\` can be attached to the root object of any archetyped structure to give the effect of a 1->N link.
 *
 * 1:1 and 1:N relationships between archetyped content elements (e.g. \`ENTRYs\`) can be expressed by using one, or more than one, respectively, \`LINKs\`. Chains of links can be used to see  problem threads  or other logical groupings of items.
 *
 * Links should be between archetyped structures only, i.e. between objects representing complete domain concepts because relationships between sub-elements of whole concepts are not necessarily meaningful, and may be downright confusing. Sensible links only exist between whole \`ENTRYs\`, \`SECTIONs\`, \`COMPOSITIONs\` and so on.
 */
export class LINK {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * Used to describe the relationship, usually in clinical terms, such as  in response to  (the relationship between test results and an order),  follow-up to  and so on. Such relationships can represent any clinically meaningful connection between pieces of information. Values for meaning include those described in Annex C, ENV 13606 pt 2 under the categories of  generic ,  documenting and reporting ,  organisational ,  clinical ,  circumstancial , and  view management .
   */
  meaning?: DV_TEXT;
  /**
   * The type attribute is used to indicate a clinical or domain-level meaning for the kind of link, for example  problem  or  issue . If type values are designed appropriately, they can be used by the requestor of EHR extracts to categorise links which must be followed and which can be broken when the extract is created.
   */
  type?: DV_TEXT;
  /**
   * The logical  to  object in the link relation, as per the linguistic sense of the meaning attribute.
   */
  target?: DV_EHR_URI;
}

/**
 * Root class of all information model classes that can be archetyped. Most classes in the openEHR reference model inherit from the \`LOCATABLE\` class, which defines the idea of  locatability in an archetyped structure. \`LOCATABLE\` defines a runtime name and an \`_archetype_node_id_\`.
 */
export abstract class LOCATABLE extends PATHABLE {
  /**
   * Runtime name of this fragment, used to build runtime paths. This is the term provided via a clinical application or batch process to name this EHR construct: its retention in the EHR faithfully preserves the original label by which this entry was known to end users.
   */
  name?: DV_TEXT;
  /**
   * Internal storage for archetype_node_id
   * @protected
   */
  protected _archetype_node_id?: openehr_base.String;

  /**
   * Design-time archetype identifier of this node taken from its generating archetype; used to build archetype paths. Always in the form of an at-code, e.g.  \`at0005\`. This value enables a 'standardised' name for this node to be generated, by referring to the generating archetype local terminology.
   *
   * At an archetype root point, the value of this attribute is always the stringified form of the \`_archetype_id_\` found in the \`_archetype_details_\` object.
   */
  get archetype_node_id(): string | undefined {
    return this._archetype_node_id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for archetype_node_id.
   * Use this to access openehr_base.String methods.
   */
  get $archetype_node_id(): openehr_base.String | undefined {
    return this._archetype_node_id;
  }

  /**
   * Sets archetype_node_id from either a primitive value or openehr_base.String wrapper.
   */
  set archetype_node_id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._archetype_node_id = undefined;
    } else if (typeof val === "string") {
      this._archetype_node_id = openehr_base.String.from(val);
    } else {
      this._archetype_node_id = val;
    }
  }

  /**
   * Optional globally unique object identifier for root points of archetyped structures.
   */
  uid?: openehr_base.UID_BASED_ID;
  /**
   * Links to other archetyped structures (data whose root object inherits from \`ARCHETYPED\`, such as \`ENTRY\`, \`SECTION\` and so on). Links may be to structures in other compositions.
   */
  links?: undefined;
  /**
   * Details of archetyping used on this node.
   */
  archetype_details?: ARCHETYPED;
  /**
   * Audit trail from non-openEHR system of original commit of information forming the content of this node, or from a conversion gateway which has synthesised this node.
   */
  feeder_audit?: FEEDER_AUDIT;
  /**
   * Clinical concept of the archetype as a whole (= derived from the archetype_node_id' of the root node)
   * @returns Result value
   */
  concept(): DV_TEXT {
    // Return the name property as the concept
    // Per openEHR specs, name is mandatory for LOCATABLE, but TypeScript allows undefined
    if (!this.name) {
      throw new Error("LOCATABLE name is required but not set");
    }
    return this.name;
  }

  /**
   * True if this node is the root of an archetyped structure.
   * @returns Result value
   */
  is_archetype_root(): openehr_base.Boolean {
    // A LOCATABLE is an archetype root if it has archetype_details
    return openehr_base.Boolean.from(this.archetype_details !== undefined);
  }
}

/**
 * Archetypes act as the configuration basis for the particular structures of instances defined by the reference model. To enable archetypes to be used to create valid data, key classes in the reference model act as  root  points for archetyping; accordingly, these classes have the \`_archetype_details_\` attribute set.
 *
 * An instance of the class \`ARCHETYPED\` contains the relevant archetype identification information, allowing generating archetypes to be matched up with data instances.
 */
export class ARCHETYPED {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * Globally unique archetype identifier.
   */
  archetype_id?: openehr_base.ARCHETYPE_ID;
  /**
   * Globally unique template identifier, if a template was active at this point in the structure. Normally, a template would only be used at the top of a top-level structure, but the possibility exists for templates at lower levels.
   */
  template_id?: openehr_base.TEMPLATE_ID;
  /**
   * Internal storage for rm_version
   * @protected
   */
  protected _rm_version?: openehr_base.String;

  /**
   * Version of the openEHR reference model used to create this object. Expressed in terms of the release version string, e.g.  1.0 ,  1.2.4 .
   */
  get rm_version(): string | undefined {
    return this._rm_version?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for rm_version.
   * Use this to access openehr_base.String methods.
   */
  get $rm_version(): openehr_base.String | undefined {
    return this._rm_version;
  }

  /**
   * Sets rm_version from either a primitive value or openehr_base.String wrapper.
   */
  set rm_version(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._rm_version = undefined;
    } else if (typeof val === "string") {
      this._rm_version = openehr_base.String.from(val);
    } else {
      this._rm_version = val;
    }
  }
}

/**
 * The \`FEEDER_AUDIT\` class defines the semantics of an audit trail which is constructed to describe the origin of data that have been transformed into openEHR form and committed to the system.
 */
export class FEEDER_AUDIT {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * Identifiers used for the item in the originating system, e.g. filler and placer ids.
   */
  originating_system_item_ids?: undefined;
  /**
   * Identifiers used for the item in the feeder system, where the feeder system is distinct from the originating system.
   */
  feeder_system_item_ids?: undefined;
  /**
   * Optional inline inclusion of or reference to original content corresponding to the openEHR content at this node. Typically a URI reference to a document or message in a persistent store associated with the EHR.
   */
  original_content?: DV_ENCAPSULATED;
  /**
   * Any audit information for the information item from the originating system.
   */
  originating_system_audit?: FEEDER_AUDIT_DETAILS;
  /**
   * Any audit information for the information item from the feeder system, if different from the originating system.
   */
  feeder_system_audit?: FEEDER_AUDIT_DETAILS;
}

/**
 * Audit details for any system in a feeder system chain. Audit details here means the general notion of who/where/when the information item to which the audit is attached was created. None of the attributes is defined as mandatory, however, in different scenarios, various combinations of attributes will usually be mandatory. This can be controlled by specifying feeder audit details in legacy archetypes.
 */
export class FEEDER_AUDIT_DETAILS {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * Internal storage for system_id
   * @protected
   */
  protected _system_id?: openehr_base.String;

  /**
   * Identifier of the system which handled the information item. This is the IT system owned by the organisation legally responsible for handling the data, and at which the data were previously created or passed by an earlier system.
   */
  get system_id(): string | undefined {
    return this._system_id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for system_id.
   * Use this to access openehr_base.String methods.
   */
  get $system_id(): openehr_base.String | undefined {
    return this._system_id;
  }

  /**
   * Sets system_id from either a primitive value or openehr_base.String wrapper.
   */
  set system_id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._system_id = undefined;
    } else if (typeof val === "string") {
      this._system_id = openehr_base.String.from(val);
    } else {
      this._system_id = val;
    }
  }

  /**
   * Identifier of the particular site/facility within an organisation which handled the item. For computability, this identifier needs to be e.g. a PKI identifier which can be included in the identifier list of the \`PARTY_IDENTIFIED\` object.
   */
  location?: PARTY_IDENTIFIED;
  /**
   * Identifiers for subject of the received information item.
   */
  subject?: PARTY_PROXY;
  /**
   * Optional provider(s) who created, committed, forwarded or otherwise handled the item.
   */
  provider?: PARTY_IDENTIFIED;
  /**
   * Time of handling the item. For an originating system, this will be time of creation, for an intermediate feeder system, this will be a time of accession or other time of handling, where available.
   */
  time?: DV_DATE_TIME;
  /**
   * Internal storage for version_id
   * @protected
   */
  protected _version_id?: openehr_base.String;

  /**
   * Any identifier used in the system such as  "interim" ,  "final" , or numeric versions if available.
   */
  get version_id(): string | undefined {
    return this._version_id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for version_id.
   * Use this to access openehr_base.String methods.
   */
  get $version_id(): openehr_base.String | undefined {
    return this._version_id;
  }

  /**
   * Sets version_id from either a primitive value or openehr_base.String wrapper.
   */
  set version_id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._version_id = undefined;
    } else if (typeof val === "string") {
      this._version_id = openehr_base.String.from(val);
    } else {
      this._version_id = val;
    }
  }

  /**
   * Optional attribute to carry any custom meta-data. May be archetyped.
   */
  other_details?: ITEM_STRUCTURE;
}
